import React, { Dispatch, FC, SetStateAction } from 'react'
import { FiltersPanel } from 'components/SA/Filters'
import URLConnector, { ConnectorProps as URLConnectorProps } from '../connectors/Url'
import { FacetsConnector, FacetsConnectorProps } from 'connectors/Facets'
import {
  SizeFacet,
  OrientationFacet,
  PriceFacet,
  CheckboxListFacet,
  ArtistCountryFacet,
  ColorFacet,
  SingleCategoryFacet,
  SortFacet,
  useBooleanFacet,
} from 'components/SA/Facets'
import {
  BROWSE_NEXT_APP_ROUTE,
  PRINT_MATERIALS_ATTRIBUTES_LIST,
  PRINT_MATERIALS_OVERRIDE_RULES,
  PRINT_PRICE_ATTRIBUTES_LIST,
} from '../helpers/constants'
import styles from './Browse.module.scss'
import Skeleton from 'components/SA/Skeleton'
import { isCategoryInstallation, isCategorySculpture } from 'components/SA/Polaroid/helpers'

type BrowseFiltersProps = URLConnectorProps &
  FacetsConnectorProps & {
    setIsFiltersVisible: Dispatch<SetStateAction<boolean>>
    closeModal?: () => void
    customClearFilterCallback?: () => void
    isFiltersLoading?: boolean
    selectedCategoryFacet: string
  }

const FiltersSkeleton = () => {
  return (
    <div className={styles.filtersSkeletonBox}>
      <Skeleton width='100%' height='40px' />
      <div className={styles.filtersSkeletonInfo}>
        <Skeleton width='80%' height='24px' />
        <Skeleton width='80%' height='24px' />
        <Skeleton width='80%' height='24px' />
      </div>
    </div>
  )
}

const BrowseFilters: FC<BrowseFiltersProps> = ({
  setIsFiltersVisible,
  closeModal,
  customClearFilterCallback,
  totalHits,
  hasPrints,
  getSelectedFacetAttributes,
  isFiltersLoading,
  selectedCategoryFacet,
}) => {
  const baseUrlPath = ''
  const materialsAttributesList = hasPrints ? PRINT_MATERIALS_ATTRIBUTES_LIST : undefined
  const isSculptureOrInstallation =
    isCategorySculpture(selectedCategoryFacet) || isCategoryInstallation(selectedCategoryFacet)

  const { changeUrlByBooleanFacet } = useBooleanFacet({
    facetName: 'is_outdoor',
    baseUrl: baseUrlPath,
    nextAppRoute: BROWSE_NEXT_APP_ROUTE,
  })
  const handleIsOutdoorToggle = (isOutdoorSafe) => {
    changeUrlByBooleanFacet(isOutdoorSafe)
  }

  const sortFilter = {
    title: 'Sort',
    content: <SortFacet baseUrl={baseUrlPath} nextAppRoute={BROWSE_NEXT_APP_ROUTE} />,
    isOpen: false,
  }

  const categoryFilter = {
    title: 'Category',
    content: <SingleCategoryFacet baseUrl={baseUrlPath} nextAppRoute={BROWSE_NEXT_APP_ROUTE} />,
    isOpen: true,
  }
  const styleFilter = {
    title: 'Style',
    content: (
      <CheckboxListFacet
        facetName='styles'
        baseUrl={baseUrlPath}
        nextAppRoute={BROWSE_NEXT_APP_ROUTE}
      />
    ),
    isOpen: true,
  }
  const subjectFilter = {
    title: 'Subject',
    content: (
      <CheckboxListFacet
        facetName='subject'
        baseUrl={baseUrlPath}
        nextAppRoute={BROWSE_NEXT_APP_ROUTE}
      />
    ),
    isOpen: true,
  }
  const mediumFilter = {
    title: 'Medium',
    content: (
      <CheckboxListFacet
        facetName='mediums'
        baseUrl={baseUrlPath}
        nextAppRoute={BROWSE_NEXT_APP_ROUTE}
      />
    ),
    isOpen: true,
  }
  const materialFilter = {
    title: 'Material',
    content: (
      <CheckboxListFacet
        facetName={hasPrints ? 'prints_materials' : 'materials'}
        baseUrl={baseUrlPath}
        nextAppRoute={BROWSE_NEXT_APP_ROUTE}
        attributesList={materialsAttributesList}
        overrideCheckboxTextRules={hasPrints ? PRINT_MATERIALS_OVERRIDE_RULES : undefined}
      />
    ),
    isOpen: hasPrints || getSelectedFacetAttributes('materials').length > 0,
  }
  const priceFilter = {
    title: 'Price',
    content: (
      <PriceFacet
        baseUrl={baseUrlPath}
        nextAppRoute={BROWSE_NEXT_APP_ROUTE}
        facetName={hasPrints ? 'prints_price_ranges' : 'price'}
        attributesList={hasPrints ? PRINT_PRICE_ATTRIBUTES_LIST : undefined}
        isCustomPriceEnabled={true}
      />
    ),
    isOpen: hasPrints || getSelectedFacetAttributes('price').length > 0,
  }
  const sizeFilter = {
    title: 'Size',
    content: (
      <SizeFacet
        baseUrl={baseUrlPath}
        nextAppRoute={BROWSE_NEXT_APP_ROUTE}
        facetName={hasPrints ? 'prints_size_ranges' : 'size'}
        isCustomSizeEnabled={true}
      />
    ),
    isOpen: hasPrints || getSelectedFacetAttributes('size').length > 0,
  }
  const isOutdoorFilter = {
    title: 'Outdoor Safe',
    isOpen: getSelectedFacetAttributes('is_outdoor').length > 0,
    isToggle: true,
    onClick: handleIsOutdoorToggle,
  }
  const orientationFilter = {
    title: 'Orientation',
    content: <OrientationFacet baseUrl={baseUrlPath} nextAppRoute={BROWSE_NEXT_APP_ROUTE} />,
    isOpen: getSelectedFacetAttributes('orientation').length > 0,
  }
  const colorFilter = {
    title: 'Color',
    content: <ColorFacet baseUrl={baseUrlPath} nextAppRoute={BROWSE_NEXT_APP_ROUTE} />,
    isOpen: getSelectedFacetAttributes('color').length > 0,
  }
  const artistCountryFilter = {
    title: 'Artist Country',
    content: <ArtistCountryFacet baseUrl={baseUrlPath} nextAppRoute={BROWSE_NEXT_APP_ROUTE} />,
    isOpen: getSelectedFacetAttributes('country').length > 0,
  }
  const featuredArtistFilter = {
    title: 'Featured Artist',
    content: (
      <CheckboxListFacet
        facetName='artist_recognition'
        baseUrl={baseUrlPath}
        nextAppRoute={BROWSE_NEXT_APP_ROUTE}
      />
    ),
    isOpen: getSelectedFacetAttributes('artist_recognition').length > 0,
  }

  const finalFilters = hasPrints
    ? [
        sortFilter,
        materialFilter,
        categoryFilter,
        styleFilter,
        subjectFilter,
        priceFilter,
        sizeFilter,
        orientationFilter,
        colorFilter,
        artistCountryFilter,
        featuredArtistFilter,
      ]
    : [
        sortFilter,
        categoryFilter,
        styleFilter,
        subjectFilter,
        mediumFilter,
        materialFilter,
        priceFilter,
        sizeFilter,
        ...(isSculptureOrInstallation ? [isOutdoorFilter] : []),
        orientationFilter,
        colorFilter,
        artistCountryFilter,
        featuredArtistFilter,
      ]

  if (isFiltersLoading) {
    return (
      <div className={styles.filtersSkeletonContainer}>
        {Array.from({ length: 12 }, (_, index) => (
          <FiltersSkeleton key={index.toString()} />
        ))}
      </div>
    )
  }

  return (
    <FiltersPanel
      filters={finalFilters}
      baseUrl={baseUrlPath}
      nextAppRoute={BROWSE_NEXT_APP_ROUTE}
      setIsFiltersVisible={setIsFiltersVisible}
      closeModal={closeModal}
      totalFiltered={totalHits}
      customClearFilterCallback={customClearFilterCallback}
    />
  )
}

export default FacetsConnector(URLConnector(BrowseFilters))
